import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ClipboardList, Info, Mail, MessageSquare, X, CheckCircle2, Clock, AlertCircle, Video, FileText } from 'lucide-react';
import ResultsClient from '../data/api/ResultsClient';
import ScheduleClient from '../../interviewSchedule/data/api/ScheduleClient';
import LearnerClient from '../../learnerControl/data/api/LearnerClient';
import { getResumePreviewUrl, getResumeDownloadUrl } from '../../../services/resumeFileService';
import ResumeViewer, { ResumeZoomControls, ResumeViewerHandle } from '../../../shared/components/ResumeViewer';
import ScheduleResultsCard from '../components/ScheduleResultsCard';
import Loader from '../../../shared/components/Loader';
import { CustomDropdown } from '../../../shared/components/CustomDropdown';
import '../styles/ResultsStyles.css';

const InterviewResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Schedule View State
  const [schedules, setSchedules] = useState<any[]>([]);
  const [rounds, setRounds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sessionName, setSessionName] = useState(location.state?.sessionName || '');
  const [roundIdFilter, setRoundIdFilter] = useState('');

  // Expert Schedule Modal State
  const [selectedExpertSchedule, setSelectedExpertSchedule] = useState<any>(null);
  const [showExpertModal, setShowExpertModal] = useState<boolean>(false);
  const [expertTab, setExpertTab] = useState<'feedback' | 'recording' | 'transcript'>('feedback');

  // Tab State
  const [activeTab, setActiveTab] = useState<'schedule' | 'applicant'>(location.state?.activeTab || 'schedule');

  // Format Helpers
  const formatDuration = (totalSeconds: number): string => {
    if (!totalSeconds || totalSeconds < 0) return "0m 0s";
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    let result = "";
    if (hours > 0) result += `${hours}h `;
    result += `${minutes}m ${seconds}s`;
    return result.trim();
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strHours = String(hours).padStart(2, '0');
    return `${day}/${month}/${year}, ${strHours}:${minutes} ${ampm}`;
  };

  // Applicant View State
  const [applicants, setApplicants] = useState<any[]>([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState<any>(null);
  const [applicantSessions, setApplicantSessions] = useState<any[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [applicantSearchTerm, setApplicantSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const [showLearnerModal, setShowLearnerModal] = useState(false);
  const [modalLearner, setModalLearner] = useState<any>(null);
  const [resumePreviewUrl, setResumePreviewUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [resumeZoom, setResumeZoom] = useState(1.5);
  const resumeViewerRef = useRef<ResumeViewerHandle>(null);

  const openResumePreview = async (url: string | null | undefined) => {
    setPdfLoading(true);
    const inlineUrl = await getResumePreviewUrl(url);
    setResumePreviewUrl(inlineUrl);
  };

  useEffect(() => {
    fetchRounds();
  }, []);

  useEffect(() => {
    if (activeTab === 'schedule') {
      fetchSchedules();
    } else if (activeTab === 'applicant' && applicants.length === 0) {
      fetchApplicants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundIdFilter, activeTab]);

  useEffect(() => {
    if (applicants.length > 0 && !selectedApplicant) {
      let applicantToSelect = null;
      if (location.state?.applicantId) {
        applicantToSelect = applicants.find(a => a.candidate_id === location.state.applicantId || a.id === location.state.applicantId);
      }
      if (!applicantToSelect) {
        applicantToSelect = applicants[0];
      }
      if (applicantToSelect) {
        handleApplicantClick(applicantToSelect);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicants, location.state, selectedApplicant]);

  const fetchRounds = async () => {
    try {
      const response = await ScheduleClient.listRounds();
      setRounds(response?.rounds || []);
    } catch (error) {
      console.error("Error fetching rounds:", error);
    }
  };

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (sessionName) params.session_name = sessionName;
      if (roundIdFilter) params.round_id = roundIdFilter;

      const response = await ResultsClient.getSchedulesAttendance(params);
      setSchedules(response?.attendance_record || response?.data || response?.records || []);
    } catch (error) {
      console.error("Error fetching schedules:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicants = async () => {
    try {
      setLoadingApplicants(true);
      const response = await LearnerClient.list({ limit: 100 });
      setApplicants(response?.records || response?.data || response?.users || response?.user_list || []);
    } catch (error) {
      console.error("Error fetching applicants:", error);
    } finally {
      setLoadingApplicants(false);
    }
  };

  const fetchSessionsForApplicant = async (candidateId: string, applicantObj?: any) => {
    try {
      setLoadingSessions(true);
      const response = await ResultsClient.getUserSessions(candidateId);
      const rawSessions = response?.sessions || response?.data || response || [];
      const applicantToUse = applicantObj || selectedApplicant;
      const candidateEmail = (applicantToUse?.email_address || applicantToUse?.email || '').toLowerCase();

      // Enrich raw sessions with loaded schedule metadata
      const enriched = rawSessions.map((sess: any) => {
        const matchingSchedule = schedules.find((sch: any) =>
          (sch.schedule_id && (sch.schedule_id === sess.session_id || sch.schedule_id === sess.schedule_id)) ||
          (sch.session_name && sess.session_name && sch.session_name.toLowerCase() === sess.session_name.toLowerCase())
        );

        if (matchingSchedule) {
          return {
            ...sess,
            interview_type: matchingSchedule.interview_type || sess.interview_type || 'AI',
            attendance_status: matchingSchedule.attendance_status || sess.attendance_status,
            attended_participants: matchingSchedule.attended_participants || sess.attended_participants || [],
            interviewer_email: matchingSchedule.interviewer_email || sess.interviewer_email,
            recording_presigned_url: matchingSchedule.recording_presigned_url || sess.recording_presigned_url,
            transcript_presigned_url: matchingSchedule.transcript_presigned_url || sess.transcript_presigned_url,
            transcript_content: matchingSchedule.transcript_content || sess.transcript_content,
            target_users: matchingSchedule.target_users || sess.target_users,
            expert_feedback: matchingSchedule.expert_feedback,
            interview_expert_score: matchingSchedule.interview_expert_score
          };
        }
        return sess;
      });

      // Also append any Expert schedules assigned to this candidate email
      if (candidateEmail) {
        schedules.forEach((sch: any) => {
          if (sch.interview_type === 'EXPERT') {
            const targets = Array.isArray(sch.target_users) ? sch.target_users : [sch.target_users];
            const isMatch = targets.some((t: any) => typeof t === 'string' && t.toLowerCase().includes(candidateEmail));
            const alreadyInEnriched = enriched.some((s: any) => s.session_id === sch.schedule_id || s.schedule_id === sch.schedule_id);

            if (isMatch && !alreadyInEnriched) {
              enriched.push({
                session_id: sch.schedule_id,
                schedule_id: sch.schedule_id,
                session_name: sch.session_name,
                created_at: sch.start_datetime,
                submitted_at: sch.end_datetime,
                duration: sch.duration,
                round_type: sch.round_type,
                interview_type: 'EXPERT',
                attendance_status: sch.attendance_status || 'not_attended',
                attended_participants: sch.attended_participants || [],
                interviewer_email: sch.interviewer_email,
                recording_presigned_url: sch.recording_presigned_url,
                transcript_presigned_url: sch.transcript_presigned_url,
                transcript_content: sch.transcript_content,
                target_users: sch.target_users,
                expert_feedback: sch.expert_feedback,
                interview_expert_score: sch.interview_expert_score
              });
            }
          }
        });
      }

      setApplicantSessions(enriched);
    } catch (error) {
      console.error("Error fetching applicant sessions:", error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      fetchSchedules();
    }
  };

  const handleCardClick = (scheduleItem: any) => {
    if (scheduleItem.interview_type === 'EXPERT') {
      setSelectedExpertSchedule(scheduleItem);
      setExpertTab('feedback');
      setShowExpertModal(true);
    } else {
      const schedId = scheduleItem.schedule_id || scheduleItem.id;
      navigate(`/dashboard/results/learners`, { state: { scheduleId: schedId } });
    }
  };

  const handleApplicantClick = (applicant: any) => {
    setSelectedApplicant(applicant);
    fetchSessionsForApplicant(applicant.candidate_id || applicant.id, applicant);
  };

  const roundOptions = useMemo(() => [
    { value: '', label: 'All Rounds' },
    ...rounds.map(r => ({ value: r.round_id, label: r.name })),
  ], [rounds]);

  return (
    <motion.div className="learner-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
      <Loader isLoading={loading && activeTab === 'schedule'} message="Loading schedules..." />
      <Loader isLoading={loadingApplicants} message="Loading applicants..." />

      {/* ── Hero ── */}
      {!selectedExpertSchedule && (
        <div className="lp-hero" style={{ flexShrink: 0 }}>
          <div className="lp-hero-left">
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#0f172a' }}>Interview Results</h1>
            <span className="lp-hero-chip" style={{ color: '#64748b', fontSize: '14px' }}>Monitor attendance and evaluate candidate performance across all interview schedules.</span>
          </div>

          <div className="view-toggle-container">
            <button
              className={`view-toggle-btn ${activeTab === 'schedule' ? 'active' : ''}`}
              onClick={() => setActiveTab('schedule')}
            >
              Schedule View
            </button>
            <button
              className={`view-toggle-btn ${activeTab === 'applicant' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('applicant');
                if (applicants.length === 0) fetchApplicants();
              }}
            >
              Applicant View
            </button>
          </div>
        </div>
      )}

      {/* ── Schedule View ── */}
      {activeTab === 'schedule' && (
        selectedExpertSchedule ? (
          /* Full Page View for Expert Driven Interview */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
            {/* Back Button */}
            <button
              onClick={() => setSelectedExpertSchedule(null)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '10px',
                padding: '10px 18px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#334155',
                cursor: 'pointer',
                width: 'fit-content',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                transition: 'all 0.2s'
              }}
            >
              ← Back to All Schedules
            </button>

            {/* Expert Interview Full Page Container */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>

              {/* Top Summary Banner */}
              {(() => {
                const isMsTeamsExpert = Boolean(
                  selectedExpertSchedule.meeting_id ||
                  (selectedExpertSchedule.meeting_link && selectedExpertSchedule.meeting_link.includes('teams.'))
                );

                const nonTeamsParticipants = [
                  ...(selectedExpertSchedule.interviewer_email ? [selectedExpertSchedule.interviewer_email] : []),
                  ...(Array.isArray(selectedExpertSchedule.target_users) ? selectedExpertSchedule.target_users : (selectedExpertSchedule.target_users ? [selectedExpertSchedule.target_users] : []))
                ].filter(Boolean).join(', ');

                return (
                  <>
                    <div style={{ background: 'linear-gradient(135deg, #15803d 0%, #0d9488 100%)', color: '#fff', padding: '24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <span style={{ backgroundColor: 'rgba(99, 102, 241, 0.3)', color: '#e0e7ff', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px', border: '1px solid rgba(199, 210, 254, 0.4)', textTransform: 'uppercase' }}>
                          Expert Driven Interview {isMsTeamsExpert ? '(MS Teams)' : ''}
                        </span>
                      </div>

                      <h2 style={{ fontSize: '22px', fontWeight: 700, margin: '0 0 16px 0', color: '#ffffff' }}>{selectedExpertSchedule.session_name}</h2>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', fontSize: '13px', color: '#e2e8f0' }}>
                        <div>
                          <span style={{ opacity: 0.8, fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Candidates</span>
                          <span style={{ fontWeight: 600, color: '#ffffff' }}>
                            {Array.isArray(selectedExpertSchedule.target_users) ? selectedExpertSchedule.target_users.join(', ') : (selectedExpertSchedule.target_users || 'N/A')}
                          </span>
                        </div>
                        <div>
                          <span style={{ opacity: 0.8, fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Interviewer Email</span>
                          <span style={{ fontWeight: 600, color: '#ffffff' }}>{selectedExpertSchedule.interviewer_email || 'N/A'}</span>
                        </div>
                        <div>
                          <span style={{ opacity: 0.8, fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Round & Duration</span>
                          <span style={{ fontWeight: 600, color: '#ffffff' }}>{selectedExpertSchedule.round_type || 'Technical'} ({selectedExpertSchedule.duration || 30} mins)</span>
                        </div>
                        <div>
                          <span style={{ opacity: 0.8, fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Attended Participants</span>
                          <span style={{ fontWeight: 600, color: '#ffffff' }}>
                            {isMsTeamsExpert ? (
                              Array.isArray(selectedExpertSchedule.attended_participants) && selectedExpertSchedule.attended_participants.length > 0
                                ? selectedExpertSchedule.attended_participants.join(', ')
                                : (selectedExpertSchedule.attendance_status === 'attended' ? 'Participants Joined' : 'None')
                            ) : (
                              nonTeamsParticipants || 'N/A'
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Navigation Bar */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc', padding: '0 24px' }}>
                      <button
                        onClick={() => setExpertTab('feedback')}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '16px 24px',
                          fontSize: '14px',
                          fontWeight: 600,
                          border: 'none',
                          borderBottom: expertTab === 'feedback' ? '3px solid #16a34a' : '3px solid transparent',
                          color: expertTab === 'feedback' ? '#15803d' : '#64748b',
                          backgroundColor: expertTab === 'feedback' ? '#ffffff' : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <ClipboardList size={16} /> Expert Feedback
                      </button>
                      {isMsTeamsExpert && (
                        <button
                          onClick={() => setExpertTab('recording')}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '16px 24px',
                            fontSize: '14px',
                            fontWeight: 600,
                            border: 'none',
                            borderBottom: expertTab === 'recording' ? '3px solid #16a34a' : '3px solid transparent',
                            color: expertTab === 'recording' ? '#15803d' : '#64748b',
                            backgroundColor: expertTab === 'recording' ? '#ffffff' : 'transparent',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          <Video size={16} /> Video Recording
                        </button>
                      )}
                      {isMsTeamsExpert && (
                        <button
                          onClick={() => setExpertTab('transcript')}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '16px 24px',
                            fontSize: '14px',
                            fontWeight: 600,
                            border: 'none',
                            borderBottom: expertTab === 'transcript' ? '3px solid #16a34a' : '3px solid transparent',
                            color: expertTab === 'transcript' ? '#15803d' : '#64748b',
                            backgroundColor: expertTab === 'transcript' ? '#ffffff' : 'transparent',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          <MessageSquare size={16} /> Transcript
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Tab Body Content */}
              <div style={{ padding: '16px 24px', height: 'calc(100vh - 380px)', overflowY: 'auto', boxSizing: 'border-box' }}>
                {expertTab === 'feedback' && (() => {
                  const feedbackEntries = Object.values(selectedExpertSchedule.expert_feedback || {});

                  if (feedbackEntries.length === 0) {
                    return (
                      <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
                        <Clock size={40} style={{ color: '#94a3b8', marginBottom: '12px' }} />
                        <h4 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: 700, color: '#475569' }}>Evaluation Pending</h4>
                        <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>The expert interviewer has not submitted feedback yet.</p>
                      </div>
                    );
                  }

                  const getScoreColor = (score: number) => {
                    if (score >= 75) return '#16a34a';
                    if (score >= 50) return '#ca8a04';
                    return '#dc2626';
                  };

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      {/* Overall Compiled Score */}
                      {selectedExpertSchedule.interview_expert_score !== undefined && selectedExpertSchedule.interview_expert_score !== null && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px 20px' }}>
                          <div>
                            <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: 700, color: '#14532d' }}>Overall Compiled Score</h4>
                            <p style={{ margin: 0, fontSize: '12px', color: '#166534' }}>Average score from all expert evaluations</p>
                          </div>
                          <div style={{ fontSize: '28px', fontWeight: 900, color: '#15803d', display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                            {selectedExpertSchedule.interview_expert_score}
                            <span style={{ fontSize: '14px', color: '#166534', fontWeight: 600 }}>/ 100</span>
                          </div>
                        </div>
                      )}

                      {/* Interviewer Submissions */}
                      {feedbackEntries.map((fb: any, index: number) => {
                        const scoreMap = fb.scores || {};
                        const criteriaList = [
                          { label: 'Communication Skills', val: scoreMap.communication ?? 0 },
                          { label: 'Technical & Core Skills', val: scoreMap.skills ?? 0 },
                          { label: 'Module / Domain Knowledge', val: scoreMap.module_knowledge ?? 0 },
                          { label: 'Problem Solving & Analytical Ability', val: scoreMap.problem_solving ?? 0 },
                          { label: 'Overall Professionalism & Soft Skills', val: scoreMap.professionalism ?? 0 }
                        ];

                        return (
                          <div key={index} style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                            {/* Card Header */}
                            <div style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                              <div>
                                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Interviewer</span>
                                <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 700 }}>{fb.interviewer_email}</span>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Submitted At</span>
                                <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600 }}>
                                  {fb.submitted_at ? new Date(fb.submitted_at).toLocaleString() : 'N/A'}
                                </span>
                              </div>
                            </div>

                            {/* Card Body */}
                            <div style={{ padding: '20px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                                {/* Category Scores */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                  <h5 style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category Scores</h5>
                                  {criteriaList.map((crit, cIdx) => (
                                    <div key={cIdx}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                                        <span style={{ color: '#1e293b', fontWeight: 500 }}>{crit.label}</span>
                                        <span style={{ color: getScoreColor(crit.val), fontWeight: 700 }}>{crit.val} / 100</span>
                                      </div>
                                      <div style={{ height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${crit.val}%`, backgroundColor: getScoreColor(crit.val), borderRadius: '3px', transition: 'width 0.3s ease' }} />
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Comments & Overall Summary */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', borderLeft: '1px solid #f1f5f9', paddingLeft: '20px' }}>
                                  <div>
                                    <h5 style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Interviewer Average</h5>
                                    <div style={{ fontSize: '24px', fontWeight: 800, color: getScoreColor(fb.interviewer_average), display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                                      {fb.interviewer_average}
                                      <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 600 }}>/ 100</span>
                                    </div>
                                  </div>

                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <h5 style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Comments / Remarks</h5>
                                    <div style={{ flex: 1, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 16px', fontSize: '13px', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontStyle: fb.comments ? 'normal' : 'italic' }}>
                                      {fb.comments ? fb.comments.trim() : 'No comments provided by the interviewer.'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {expertTab === 'recording' && (
                  <div style={{ backgroundColor: '#090d16', borderRadius: '12px', padding: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxWidth: '640px', margin: '0 auto' }}>
                    {selectedExpertSchedule.recording_presigned_url ? (
                      <video
                        controls
                        style={{ width: '100%', maxHeight: '250px', borderRadius: '8px', objectFit: 'contain' }}
                        src={selectedExpertSchedule.recording_presigned_url}
                      />
                    ) : (
                      <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                        No video recording available for this expert session yet.
                      </div>
                    )}
                  </div>
                )}

                {expertTab === 'transcript' && (() => {
                  const rawContent = selectedExpertSchedule.transcript_content || "";
                  const parseTranscriptBlocks = (textStr: string) => {
                    if (!textStr) return [];
                    const lines = textStr.split(/\r?\n/);
                    const blocks: { speaker: string; text: string; timestamp?: string }[] = [];
                    let currentTimestamp = "";

                    for (let i = 0; i < lines.length; i++) {
                      const line = lines[i].trim();
                      if (!line || line.startsWith("WEBVTT") || line.match(/^\d+$/)) continue;

                      if (line.includes("-->")) {
                        currentTimestamp = line.split("-->")[0].trim();
                        continue;
                      }

                      const speakerMatch = line.match(/<v\s+([^>]+)>(.*?)<\/v>/i) || line.match(/<v\s+([^>]+)>(.*)/i);
                      if (speakerMatch) {
                        const speakerName = speakerMatch[1].trim();
                        const textContent = speakerMatch[2].replace(/<\/v>/gi, "").trim();
                        if (textContent) {
                          blocks.push({
                            speaker: speakerName,
                            text: textContent,
                            timestamp: currentTimestamp
                          });
                        }
                      } else {
                        const colonIdx = line.indexOf(":");
                        if (colonIdx > 0 && colonIdx < 30 && !line.startsWith("http")) {
                          const speakerName = line.substring(0, colonIdx).trim();
                          const textContent = line.substring(colonIdx + 1).trim();
                          blocks.push({
                            speaker: speakerName,
                            text: textContent,
                            timestamp: currentTimestamp
                          });
                        } else if (line) {
                          if (blocks.length > 0 && !currentTimestamp) {
                            blocks[blocks.length - 1].text += " " + line;
                          } else {
                            blocks.push({
                              speaker: "Speaker",
                              text: line,
                              timestamp: currentTimestamp
                            });
                          }
                        }
                      }
                    }
                    return blocks;
                  };

                  const parsedBlocks = parseTranscriptBlocks(rawContent);

                  return (
                    <div
                      style={{
                        backgroundColor: '#f8fafc',
                        borderRadius: '12px',
                        padding: '20px',
                        border: '1px solid #e2e8f0',
                        width: '100%',
                        height: 'calc(100vh - 380px)',
                        maxHeight: '440px',
                        minHeight: '280px',
                        overflowY: 'auto',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px'
                      }}
                    >
                      {parsedBlocks.length > 0 ? (
                        parsedBlocks.map((blk, idx) => (
                          <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 700, fontSize: '13px', color: '#15803d' }}>
                                {blk.speaker}
                              </span>
                              {blk.timestamp && (
                                <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500 }}>
                                  {blk.timestamp}
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                padding: '14px 18px',
                                fontSize: '14px',
                                color: '#334155',
                                lineHeight: '1.6',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                              }}
                            >
                              {blk.text}
                            </div>
                          </div>
                        ))
                      ) : (
                        <pre style={{ margin: 0, fontFamily: 'inherit', fontSize: '14px', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>
                          {rawContent || "No transcript content available for this expert session."}
                        </pre>
                      )}
                    </div>
                  );
                })()}
              </div>

            </div>
          </div>
        ) : (
          <div className="lp-table-panel">
            {/* Toolbar */}
            <div className="lp-table-panel-head" style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '8px 16px', gap: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '8px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ClipboardList size={16} strokeWidth={2.5} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ fontSize: '1rem', color: '#64748b', fontWeight: 600, textTransform: 'none', letterSpacing: '0.5px' }}>Total Schedules:</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', lineHeight: 1, letterSpacing: '-0.5px' }}>{schedules.length}</div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                {/* Search */}
                <div className="lp-search" style={{ height: 40, width: 260, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', position: 'relative' }}>
                  <Search size={16} color="#94a3b8" />
                  <input
                    type="text"
                    placeholder="Search by session name..."
                    value={sessionName}
                    onChange={e => setSessionName(e.target.value)}
                    onKeyDown={handleSearch}
                    style={{ paddingRight: sessionName ? '30px' : '10px' }}
                  />
                  {sessionName && (
                    <button
                      style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: '#f1f5f9', cursor: 'pointer', color: '#64748b', width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                      onClick={() => { setSessionName(''); fetchSchedules(); }}
                    >
                      <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </div>

                <div style={{ minWidth: '180px' }}>
                  <CustomDropdown
                    options={roundOptions}
                    value={roundIdFilter}
                    onChange={setRoundIdFilter}
                    placeholder="All Rounds"
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: '24px', maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
              {schedules.length === 0 && !loading ? (
                <div className="is-empty" style={{ textAlign: 'center', padding: '48px 0' }}>
                  <ClipboardList size={48} color="#cbd5e1" style={{ margin: '0 auto 16px' }} />
                  <h3 style={{ color: '#0f172a', fontSize: '1.1rem', marginBottom: '8px' }}>No Schedules Found</h3>
                  <p style={{ color: '#64748b', fontSize: '0.9rem' }}>There are no interview schedules matching your search criteria.</p>
                </div>
              ) : (
                <div className="results-grid">
                  {schedules.map((schedule: any, index: number) => (
                    <ScheduleResultsCard
                      key={schedule.schedule_id || schedule.id || index}
                      schedule={schedule}
                      onClick={() => handleCardClick(schedule)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* ── Applicant View ── */}
      {activeTab === 'applicant' && (() => {
        const filteredApplicants = applicants.filter((a: any) => {
          const name = `${a.first_name || ''} ${a.last_name || ''} ${a.name || ''}`.toLowerCase();
          const email = (a.email_address || a.email || '').toLowerCase();
          const search = applicantSearchTerm.toLowerCase();
          return name.includes(search) || email.includes(search);
        });

        return (
          <div className="applicant-view-container" style={{ display: 'flex', gap: '24px', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* Left Panel: Applicants List */}
            <div className="applicant-list-panel" style={{ width: '350px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {!showSearch ? (
                <div style={{ padding: '16px 16px 16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontWeight: 500, color: '#334155', fontSize: '16px' }}>Applicants ({filteredApplicants.length})</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setShowSearch(true)} style={{ width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Search size={16} color="#475569" /></button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '16px 16px 16px 24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="lp-search" style={{ height: 40, flex: 1, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', position: 'relative' }}>
                    <Search size={16} color="#94a3b8" />
                    <input
                      type="text"
                      placeholder="Search applicants..."
                      value={applicantSearchTerm}
                      onChange={e => setApplicantSearchTerm(e.target.value)}
                      style={{ paddingRight: applicantSearchTerm ? '30px' : '10px' }}
                      autoFocus
                    />
                    {applicantSearchTerm && (
                      <button
                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: '#f1f5f9', cursor: 'pointer', color: '#64748b', width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                        onClick={() => setApplicantSearchTerm('')}
                      >
                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>
                  <button onClick={() => { setShowSearch(false); setApplicantSearchTerm(''); }} style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none' }}>
                    <X size={20} color="#64748b" />
                  </button>
                </div>
              )}

              <div style={{ overflowY: 'auto', flex: 1, padding: '0 16px 16px 24px' }}>
                {loadingApplicants ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}></div>
                ) : filteredApplicants.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>No applicants found.</div>
                ) : (
                  filteredApplicants.map((applicant: any, index: number) => {
                    const isSelected = selectedApplicant?.candidate_id === applicant.candidate_id;
                    const fullName = `${applicant.first_name || ''} ${applicant.last_name || ''}`.trim() || applicant.name || 'Unknown Candidate';

                    let initials = 'U';
                    if (applicant.first_name && applicant.last_name) {
                      initials = `${applicant.first_name[0]}${applicant.last_name[0]}`.toUpperCase();
                    } else if (applicant.first_name && applicant.first_name.length > 1) {
                      initials = applicant.first_name.substring(0, 2).toUpperCase();
                    } else if (applicant.name) {
                      const parts = applicant.name.split(' ');
                      if (parts.length > 1) {
                        initials = `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                      } else {
                        initials = applicant.name.substring(0, 2).toUpperCase();
                      }
                    } else if (applicant.first_name) {
                      initials = applicant.first_name[0].toUpperCase();
                    }

                    return (
                      <div
                        key={applicant.candidate_id || index}
                        onClick={() => handleApplicantClick(applicant)}
                        style={{
                          padding: '12px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          cursor: 'pointer',
                          borderRadius: '12px',
                          marginBottom: '12px',
                          transition: 'all 0.2s',
                          position: 'relative'
                        }}
                        className={`applicant-list-item ${isSelected ? 'selected' : ''}`}
                      >
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: '#a7f3d0',
                          color: '#2d6a4f',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          flexShrink: 0
                        }}>
                          {initials}
                        </div>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div style={{ fontWeight: 500, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', fontSize: '14px' }}>{fullName}</div>
                          <div style={{ fontSize: '12px', opacity: 0.8, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', marginTop: '2px' }}>{applicant.email_address || applicant.email || ''}</div>
                        </div>

                        <button
                          className="applicant-info-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setModalLearner(applicant);
                            setShowLearnerModal(true);
                          }}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: 'inherit',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Info size={18} />
                        </button>
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            {/* Right Panel: Attended Sessions */}
            <div className="applicant-sessions-panel" style={{ flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', display: 'flex', flexDirection: 'column', minHeight: 0, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.02)', overflow: 'hidden' }}>
              {!selectedApplicant ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', padding: '24px' }}>
                  <ClipboardList size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                  <h3 style={{ margin: '0 0 8px 0', color: '#0f172a' }}>Select an applicant</h3>
                  <p style={{ margin: 0 }}>Click on an applicant from the list to view their attended sessions.</p>
                </div>
              ) : loadingSessions ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Loader isLoading={true} message="Loading sessions..." />
                </div>
              ) : applicantSessions.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '32px', textAlign: 'center' }}>
                  <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                    <ClipboardList size={36} color="#22c55e" />
                  </div>
                  <h3 style={{ margin: '0 0 8px 0', color: '#0f172a', fontSize: '18px', fontWeight: 700 }}>No Sessions Yet</h3>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '14px', lineHeight: 1.6, maxWidth: 260 }}>This applicant hasn't attended any interview sessions yet.</p>
                </div>
              ) : (
                <>
                  <div style={{ padding: '24px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
                    <div className="sessions-grid">
                      {applicantSessions.map((session: any, index: number) => {
                        const status = session.status?.toUpperCase();
                        const isScored = status === 'SCORED';
                        const isFailed = status === 'FAILED';
                        const isMissed = status === 'MISSED';
                        const isInProgress = ['IN PROGRESS', 'IN_PROGRESS', 'INITIATED'].includes(status);
                        const isCompleted = status === 'COMPLETED';

                        const isExpert = session.interview_type?.toUpperCase() === 'EXPERT';
                        const isAttended = session.attendance_status === 'attended' ||
                          (Array.isArray(session.attended_participants) && session.attended_participants.length > 0);
                        const attendedNames = Array.isArray(session.attended_participants) && session.attended_participants.length > 0
                          ? session.attended_participants.join(', ')
                          : (isAttended ? 'Participants Joined' : 'No one');

                        const getStatusBadge = () => {
                          if (isExpert) return null;
                          if (isScored) return <span className="status-badge status-scored"><CheckCircle2 size={13} /> Scored</span>;
                          if (isFailed) return <span className="status-badge status-failed"><AlertCircle size={13} /> Failed</span>;
                          if (isMissed) return <span className="status-badge status-failed"><AlertCircle size={13} /> Missed</span>;
                          if (isInProgress) return <span className="status-badge status-inprogress"><Clock size={13} /> In Progress</span>;
                          if (isCompleted) return <span className="status-badge status-pending"><Clock size={13} /> Completed</span>;
                          return <span className="status-badge status-pending">{session.status || 'Pending'}</span>;
                        };

                        const btnClass = isScored ? 'btn-results' : isMissed ? 'btn-missed' : 'btn-disabled';
                        const btnLabel = isScored
                          ? 'Show Results →'
                          : isMissed
                            ? 'Missed'
                            : isFailed
                              ? 'Evaluation Failed'
                              : isInProgress
                                ? 'Interview in Progress'
                                : isCompleted
                                  ? 'Evaluating Results'
                                  : 'Evaluation Pending';
                        const btnDisabled = !isScored;

                        return (
                          <div key={session.session_id || index} className="session-card">
                            <div className="session-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span className="session-round-name">{session.round_name || session.round_type || 'Technical Round'}</span>
                                <span style={{
                                  backgroundColor: isExpert ? '#e0e7ff' : '#dcfce7',
                                  color: isExpert ? '#4338ca' : '#15803d',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  border: `1px solid ${isExpert ? '#c7d2fe' : '#bbf7d0'}`,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  {isExpert ? 'Expert' : 'AI'}
                                </span>
                              </div>
                              {getStatusBadge()}
                            </div>

                            <h3 className="session-title" style={{ marginTop: '6px' }}>{session.session_name || 'Interview Session'}</h3>

                            <div className="session-meta">
                              {session.created_at && (
                                <div className="meta-item">
                                  <strong>Started on:</strong> {formatDateTime(session.created_at)}
                                </div>
                              )}
                              {session.submitted_at && (
                                <div className="meta-item">
                                  <strong>{isMissed ? 'Expired on:' : 'Submitted:'}</strong> {formatDateTime(session.submitted_at)}
                                </div>
                              )}
                              {session.duration && (
                                <div className="meta-item">
                                  <strong>Duration:</strong> {formatDuration(session.duration)}
                                </div>
                              )}
                              {isExpert && (
                                <div className="meta-item" style={{ marginTop: '4px', color: isAttended ? '#15803d' : '#e11d48' }}>
                                  <strong>Attended by:</strong> {isAttended ? attendedNames : 'No one'}
                                </div>
                              )}
                            </div>

                            {isScored && session.score !== undefined && !isExpert && (
                              <div className="session-score-container">
                                <div className="score-label">
                                  <span>Overall Performance</span>
                                  <span>{session.score}/10</span>
                                </div>
                                <div className="score-bar">
                                  <div className="score-fill" style={{ width: `${(session.score || 0) * 10}%` }} />
                                </div>
                              </div>
                            )}

                            {isExpert ? (
                              <div className="session-actions">
                                <button
                                  className={`action-btn ${isAttended ? 'btn-results' : 'btn-disabled'}`}
                                  disabled={!isAttended}
                                  onClick={() => {
                                    if (isAttended) {
                                      setSelectedExpertSchedule(session);
                                      setExpertTab('feedback');
                                      setActiveTab('schedule');
                                    }
                                  }}
                                  style={!isAttended ? { opacity: 0.6, cursor: 'not-allowed', backgroundColor: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0' } : {}}
                                >
                                  {isAttended ? 'View Expert Results →' : 'Attended by No one'}
                                </button>
                              </div>
                            ) : (
                              <div className="session-actions">
                                <button
                                  className={`action-btn ${btnClass}`}
                                  disabled={btnDisabled}
                                  onClick={() => {
                                    if (isScored && session.session_id) {
                                      navigate(`/dashboard/results/stats`, {
                                        state: {
                                          scheduleId: session.schedule_id || session.session_id,
                                          sessionId: session.session_id,
                                          candidateId: selectedApplicant.candidate_id,
                                          returnTab: 'applicant'
                                        }
                                      });
                                    }
                                  }}
                                >
                                  {btnLabel}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Applicant Information Modal ── */}
      {showLearnerModal && modalLearner && (() => {
        const fullName = `${modalLearner.first_name || ''} ${modalLearner.last_name || ''}`.trim() || modalLearner.name || 'Unknown Candidate';
        let initials = 'U';
        if (modalLearner.first_name && modalLearner.last_name) {
          initials = `${modalLearner.first_name[0]}${modalLearner.last_name[0]}`.toUpperCase();
        } else if (modalLearner.first_name && modalLearner.first_name.length > 1) {
          initials = modalLearner.first_name.substring(0, 2).toUpperCase();
        } else if (modalLearner.name) {
          const parts = modalLearner.name.split(' ');
          if (parts.length > 1) {
            initials = `${parts[0][0]}${parts[1][0]}`.toUpperCase();
          } else {
            initials = modalLearner.name.substring(0, 2).toUpperCase();
          }
        } else if (modalLearner.first_name) {
          initials = modalLearner.first_name[0].toUpperCase();
        }

        return ReactDOM.createPortal(
          <div style={{ position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }} onClick={() => setShowLearnerModal(false)}>
            <div style={{ background: '#fff', borderRadius: '24px', width: '100%', maxWidth: '850px', padding: '32px', boxShadow: '0 24px 60px rgba(0,0,0,0.15)', position: 'relative' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#334155', margin: 0 }}>Applicant Information</h3>
                <button onClick={() => setShowLearnerModal(false)} style={{ background: '#e0f2fe', color: '#0369a1', border: 'none', borderRadius: '8px', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <X size={18} />
                </button>
              </div>

              {/* Banner */}
              <div style={{ background: '#326553', borderRadius: '20px', padding: '24px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ width: '80px', height: '80px', borderRadius: '20px', background: '#a7f3d0', color: '#2d6a4f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '24px' }}>
                    {initials}
                  </div>
                  <div style={{ color: '#fff', fontSize: '22px', fontWeight: 500 }}>{fullName}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {modalLearner.email_address && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.15)', padding: '10px 16px', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: 500 }}>
                      <Mail size={16} />
                      {modalLearner.email_address}
                    </div>
                  )}
                  <button style={{ background: 'rgba(255,255,255,0.15)', border: 'none', width: '40px', height: '40px', borderRadius: '8px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <MessageSquare size={16} />
                  </button>
                </div>
              </div>

              {/* Applicant Details */}
              <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#334155', margin: '0 0 16px 4px' }}>Applicant Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#475569', fontSize: '13px' }}>Gender:</span>
                  <span style={{ color: '#0f172a', fontWeight: 600, fontSize: '13px' }}>{modalLearner.gender || 'Not Provided'}</span>
                </div>
                <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#475569', fontSize: '13px' }}>Date of Birth:</span>
                  <span style={{ color: '#0f172a', fontWeight: 600, fontSize: '13px' }}>{modalLearner.date_of_birth || 'Not Provided'}</span>
                </div>
                <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#475569', fontSize: '13px' }}>Qualification:</span>
                  <span style={{ color: '#0f172a', fontWeight: 600, fontSize: '13px' }}>{modalLearner.education || 'Not Provided'}</span>
                </div>
                <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#475569', fontSize: '13px' }}>Status:</span>
                  <span style={{ color: '#0f172a', fontWeight: 600, fontSize: '13px' }}>{modalLearner.status || 'Active'}</span>
                </div>
                <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '8px', gridColumn: 'span 2' }}>
                  <span style={{ color: '#475569', fontSize: '13px' }}>Resume:</span>
                  {modalLearner.resume_url ? (
                    <button onClick={() => { openResumePreview(modalLearner.resume_url); }} style={{ color: '#059669', fontWeight: 600, fontSize: '13px', textDecoration: 'underline', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>View Resume</button>
                  ) : (
                    <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: '13px' }}>No Resume Attached</span>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* ── Resume PDF Viewer Modal ── */}
      {resumePreviewUrl && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '24px 80px' }}
          onClick={() => { setResumePreviewUrl(null); setPdfLoading(false); }}
        >
          <div
            style={{ background: '#fff', borderRadius: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0, borderRadius: '20px 20px 0 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '8px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px' }}>Resume Preview</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ResumeZoomControls
                  zoom={resumeZoom}
                  onZoomChange={setResumeZoom}
                  onSearchClick={() => resumeViewerRef.current?.openSearch()}
                  onDownloadClick={async () => {
                    const downloadUrl = await getResumeDownloadUrl(resumePreviewUrl);
                    if (downloadUrl) {
                      const a = document.createElement("a");
                      a.href = downloadUrl;
                      a.download = "resume";
                      a.click();
                    }
                  }}
                />
                <button
                  onClick={() => { setResumePreviewUrl(null); setPdfLoading(false); }}
                  style={{ background: '#f1f5f9', border: 'none', color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, padding: '8px 14px', borderRadius: '8px', transition: 'all 0.2s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#e2e8f0'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9'; }}
                >
                  <X size={16} /> Close
                </button>
              </div>
            </div>
            {/* Body */}
            <div style={{ flex: 1, position: 'relative' }}>
              {pdfLoading && (
                <div style={{ position: 'absolute', inset: 0, background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px', zIndex: 1 }}>
                  <div style={{ width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTopColor: '#16a34a', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 500 }}>Loading PDF...</span>
                </div>
              )}
              <ResumeViewer
                ref={resumeViewerRef}
                key={resumePreviewUrl}
                url={resumePreviewUrl}
                zoom={resumeZoom}
                onZoomChange={setResumeZoom}
                onLoaded={() => setPdfLoading(false)}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </motion.div>
  );
};

export default InterviewResultsPage;
